import React, { useMemo, useState } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Megaphone, Plus, Send, Eye, CheckCheck, Pencil, ShieldAlert } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/i18n";
import { levelDisplayName, levelWord, levelSystemOf } from "@/lib/levelLabels";
import {
  AnnouncementAttachmentList,
  AnnouncementAttachmentPicker,
  attachmentItemsFromRecord,
} from "@/components/AnnouncementAttachments";

const MANAGEMENT_ROLES = ["platform_admin", "tenant_owner", "principal", "administrator"];

const daysFromToday = (days) => {
  const value = new Date();
  value.setDate(value.getDate() + days);
  return value.toISOString().slice(0, 10);
};

const bodyHtmlToPlainText = (bodyHtml) => {
  if (!bodyHtml) return "";
  if (typeof DOMParser === "undefined") return bodyHtml;
  const document = new DOMParser().parseFromString(bodyHtml, "text/html");
  return (document.body.innerText || document.body.textContent || "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const emptyForm = (kind = "circular") => ({
  title: "",
  description: "",
  kind,
  visible_until: daysFromToday(7),
  audience_roles: ["guardian"],
  grade_id: "",
  section_id: "",
  send_email: true,
  attachments: [],
});

const COPY = {
  en: {
    typeLabel: "Announcement type",
    circularType: "Circular",
    emergencyType: "Emergency announcement",
    visibleUntil: "Visible until",
    descriptionLabel: "Description",
    descriptionHint: "Write naturally. Your paragraphs and line breaks are formatted automatically for readers.",
    descriptionPlaceholder: "Dear parents and staff,\n\nWrite the details of your announcement here.",
    readerPreview: "Reader preview",
    previewTitle: "Announcement title",
    previewDescription: "Your description will appear here.",
    publishContentHint: "Add a title and description to enable publishing.",
    publishDateHint: "Choose how long this announcement will remain visible.",
    publishSectionHint: "Choose your class section to enable publishing.",
    audienceLoadingHint: "Loading audience options...",
    editTitle: "Edit announcement",
    saveChanges: "Save changes",
    edit: "Edit",
    gradeLabel: "Grade",
    chooseGrade: "Choose grade",
    publishNow: "Publish and notify",
  },
  ar: {
    typeLabel: "نوع الإعلان",
    circularType: "تعميم",
    emergencyType: "إعلان طارئ",
    visibleUntil: "مرئي حتى",
    descriptionLabel: "وصف مختصر",
    descriptionHint: "اكتب بشكل طبيعي. سننسق الفقرات والأسطر تلقائياً للقراء.",
    descriptionPlaceholder: "أولياء الأمور والموظفون الأعزاء،\n\nاكتب تفاصيل إعلانك هنا.",
    readerPreview: "معاينة القارئ",
    previewTitle: "عنوان الإعلان",
    previewDescription: "سيظهر وصف إعلانك هنا.",
    publishContentHint: "أضف عنواناً ووصفاً لتفعيل النشر.",
    publishDateHint: "اختر مدة ظهور هذا الإعلان للقراء.",
    publishSectionHint: "اختر قسم صفك لتفعيل النشر.",
    audienceLoadingHint: "جارٍ تحميل خيارات الجمهور...",
    editTitle: "تعديل الإعلان",
    saveChanges: "حفظ التغييرات",
    edit: "تعديل",
    gradeLabel: "الصف",
    chooseGrade: "اختر الصف",
    publishNow: "نشر وإشعار",
  },
};

export default function Circulars({
  forcedKind = null,
  embedded = false,
  titleOverride = null,
  subtitleOverride = null,
}) {
  const { t, language } = useI18n();
  const copyBase = COPY[language] || COPY.en;
  const { user } = useAuth();
  const mode = levelSystemOf(user);
  const copy = {
    ...copyBase,
    gradeLabel: levelWord(mode, language),
    chooseGrade: mode === "age_band" ? (language === "ar" ? "اختر الفئة العمرية" : "Choose age group") : copyBase.chooseGrade,
  };
  const [open, setOpen] = useState(false);
  const [editingCircular, setEditingCircular] = useState(null);
  const defaultKind = forcedKind || "circular";
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["circulars", forcedKind || "all"],
    queryFn: async () => unwrap(
      await api.get("/circulars", forcedKind ? { params: { kind: forcedKind } } : undefined),
    ),
  });
  const canManageAnnouncements = MANAGEMENT_ROLES.some((role) =>
    user?.roles?.includes(role),
  );
  const teacherOnly =
    user?.roles?.includes("teacher") &&
    ![
      "platform_admin",
      "tenant_owner",
      "principal",
      "administrator",
    ].some((role) => user.roles.includes(role));
  const [form, setForm] = useState(() => emptyForm(defaultKind));
  const {
    data: teacherHome,
    isLoading: teacherSectionsLoading,
    isError: teacherSectionsError,
    error: teacherSectionsErrorDetail,
  } = useQuery({
    queryKey: ["teacher-home"],
    queryFn: async () => unwrap(await api.get("/teacher/home")),
    enabled: open && teacherOnly,
  });
  const {
    data: tenantSections,
    isLoading: tenantSectionsLoading,
    isError: tenantSectionsError,
    error: tenantSectionsErrorDetail,
  } = useQuery({
    queryKey: ["circular-sections"],
    queryFn: async () => unwrap(await api.get("/sections")),
    enabled: open && !teacherOnly,
  });
  const {
    data: tenantGrades,
    isLoading: tenantGradesLoading,
    isError: tenantGradesError,
    error: tenantGradesErrorDetail,
  } = useQuery({
    queryKey: ["circular-grades"],
    queryFn: async () => unwrap(await api.get("/grades")),
    enabled: open && !teacherOnly,
  });
  const sections = teacherOnly
    ? teacherHome?.sections || []
    : tenantSections || [];
  const grades = tenantGrades || [];
  const gradeNameById = useMemo(() => {
    const names = new Map();
    grades.forEach((grade) => {
      if (grade?.id) {
        names.set(grade.id, levelDisplayName(grade, language, grades) || grade.id);
      }
    });
    return names;
  }, [grades, language]);
  const gradeOptions = useMemo(() => {
    const byId = new Map();
    sections.forEach((section) => {
      const gradeId = section.grade_id || section.grade?.id;
      if (!gradeId || byId.has(gradeId)) return;
      byId.set(gradeId, {
        id: gradeId,
        name: levelDisplayName(section.grade, language, grades) || gradeNameById.get(gradeId) || gradeId,
      });
    });
    return Array.from(byId.values()).sort((a, b) =>
      `${a.name}`.localeCompare(`${b.name}`),
    );
  }, [gradeNameById, sections, language]);
  const sectionsLoading = teacherOnly
    ? teacherSectionsLoading
    : tenantSectionsLoading;
  const sectionsError = teacherOnly
    ? teacherSectionsError
    : tenantSectionsError;
  const sectionsErrorDetail = teacherOnly
    ? teacherSectionsErrorDetail
    : tenantSectionsErrorDetail;
  const gradeScopeLoading =
    !teacherOnly && form.audience_roles.includes("guardian")
      ? sectionsLoading || tenantGradesLoading
      : sectionsLoading;
  const gradeScopeError =
    !teacherOnly && form.audience_roles.includes("guardian")
      ? sectionsError || tenantGradesError
      : sectionsError;
  const gradeScopeErrorDetail =
    !teacherOnly && form.audience_roles.includes("guardian")
      ? sectionsErrorDetail || tenantGradesErrorDetail
      : sectionsErrorDetail;

  const selectedGradeSectionIds = !teacherOnly && form.grade_id
    ? sections
      .filter((section) => section.grade_id === form.grade_id)
      .map((section) => section.id)
    : [];

  const createMut = useMutation({
    mutationFn: async () => {
      const c = unwrap(
        await api.post("/circulars", {
          title: form.title,
          description: form.description,
          kind: forcedKind || (teacherOnly ? "circular" : form.kind),
          visible_until: form.visible_until,
          audience_json: {
            roles: teacherOnly ? ["guardian"] : form.audience_roles,
            grade_ids: teacherOnly ? [] : (form.grade_id ? [form.grade_id] : []),
            section_ids: teacherOnly
              ? (form.section_id ? [form.section_id] : [])
              : selectedGradeSectionIds,
          },
          attachment_file_ids: form.attachments.map((attachment) => attachment.id),
        }),
      );
      await api.post(`/circulars/${c.id}/publish`, {
        send_email: form.send_email,
      });
      return c;
    },
    onSuccess: () => {
      toast.success(t("staffCirculars.publishedToast"));
      setOpen(false);
      setForm(emptyForm(defaultKind));
      setEditingCircular(null);
      refetch();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const updateMut = useMutation({
    mutationFn: async () => unwrap(
      await api.patch(`/circulars/${editingCircular?.id}`, {
        title: form.title,
        description: form.description,
        visible_until: form.visible_until,
        audience_json: {
          roles: teacherOnly ? ["guardian"] : form.audience_roles,
          grade_ids: teacherOnly ? [] : (form.grade_id ? [form.grade_id] : []),
          section_ids: teacherOnly
            ? (form.section_id ? [form.section_id] : [])
            : selectedGradeSectionIds,
        },
        attachment_file_ids: form.attachments.map((attachment) => attachment.id),
      }),
    ),
    onSuccess: () => {
      toast.success(copy.saveChanges);
      setOpen(false);
      setEditingCircular(null);
      setForm(emptyForm(defaultKind));
      refetch();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const startCreate = () => {
    setEditingCircular(null);
    setForm(emptyForm(defaultKind));
    setOpen(true);
  };

  const startEdit = (circular) => {
    const audience = circular.audience_json || {};
    const selectedSectionId = audience.section_ids?.[0] || "";
    const selectedGradeId =
      audience.grade_ids?.[0]
      || sections.find((section) => section.id === selectedSectionId)?.grade_id
      || "";
    setEditingCircular(circular);
    setForm({
      title: circular.title || "",
      description: circular.description || bodyHtmlToPlainText(circular.body_html || ""),
      kind: circular.kind || defaultKind,
      visible_until: circular.visible_until || daysFromToday(7),
      audience_roles: audience.roles?.length ? audience.roles : ["guardian"],
      grade_id: teacherOnly ? "" : selectedGradeId,
      section_id: teacherOnly ? selectedSectionId : "",
      send_email: false,
      attachments: attachmentItemsFromRecord(circular),
    });
    setOpen(true);
  };

  React.useEffect(() => {
    if (teacherOnly || !editingCircular || form.grade_id || !sections.length) {
      return;
    }
    const audience = editingCircular.audience_json || {};
    const fallbackSectionId = audience.section_ids?.[0];
    if (!fallbackSectionId) {
      return;
    }
    const fallbackSection = sections.find((section) => section.id === fallbackSectionId);
    if (!fallbackSection?.grade_id) {
      return;
    }
    setForm((prev) => ({ ...prev, grade_id: fallbackSection.grade_id }));
  }, [editingCircular, form.grade_id, sections, teacherOnly]);

  const isSubmitting = createMut.isPending || updateMut.isPending;
  const previewAccent = forcedKind === "emergency" || form.kind === "emergency"
    ? "var(--error)"
    : "var(--warning)";
  const publishHint = !form.title.trim() || !form.description.trim()
    ? copy.publishContentHint
    : !form.visible_until
      ? copy.publishDateHint
      : gradeScopeLoading
        ? copy.audienceLoadingHint
        : teacherOnly && !form.section_id
          ? copy.publishSectionHint
          : null;
  const publishDisabled = Boolean(publishHint || isSubmitting);

  return (
    <div className={embedded ? "space-y-4" : "p-4 md:p-6 lg:p-8"} data-testid="circulars-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
            {titleOverride || t("staffCirculars.title")}
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            {subtitleOverride || t("staffCirculars.subtitle")}
          </p>
        </div>
        {!isError && (
          <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
              setOpen(nextOpen);
              if (!nextOpen) {
                setEditingCircular(null);
                setForm(emptyForm(defaultKind));
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                data-testid="new-circular-btn"
                onClick={startCreate}
              >
                <Plus className="h-4 w-4 mr-1.5" />{" "}
                  {forcedKind === "emergency"
                    ? t("staffCirculars.newEmergency")
                    : t("staffCirculars.newCircular")}
              </Button>
            </DialogTrigger>
            <DialogContent
              className="!fixed !inset-0 !z-50 !flex !h-auto !w-full !max-w-none !translate-x-0 !translate-y-0 !flex-col !gap-0 !overflow-hidden !border-0 !p-0 !shadow-none !rounded-none sm:!rounded-none"
              closeLabel={t("staffCirculars.close")}
            >
              <DialogHeader className="shrink-0 border-b border-[var(--subtle)] bg-background px-4 py-4 pr-12 sm:px-6 sm:pr-12">
                <DialogTitle className="font-display">
                  {editingCircular
                    ? copy.editTitle
                    : t("staffCirculars.composeCircular")}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {t("staffCirculars.subtitle")}
                </DialogDescription>
              </DialogHeader>
              <div className="mx-auto min-h-0 w-full max-w-2xl flex-1 space-y-3 overflow-x-hidden overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
                {!teacherOnly && !forcedKind && (
                  <div>
                    <Label className="text-xs uppercase font-semibold">
                      {copy.typeLabel}
                    </Label>
                    <Select
                      value={form.kind}
                      onValueChange={(value) => setForm({ ...form, kind: value })}
                      disabled={!!editingCircular || !canManageAnnouncements}
                    >
                      <SelectTrigger className="mt-1" data-testid="circular-kind">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="circular">{copy.circularType}</SelectItem>
                        {canManageAnnouncements && (
                          <SelectItem value="emergency">{copy.emergencyType}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label htmlFor="circular-title" className="text-xs uppercase font-semibold">
                    {t("staffCirculars.titleLabel")}
                  </Label>
                  <Input
                    id="circular-title"
                    value={form.title}
                    onChange={(e) =>
                      setForm({ ...form, title: e.target.value })
                    }
                    className="mt-1"
                    placeholder="Annual Sports Day"
                    data-testid="circular-title"
                  />
                </div>
                <div>
                  <Label htmlFor="circular-description" className="text-xs uppercase font-semibold">
                    {copy.descriptionLabel}
                  </Label>
                  <Textarea
                    id="circular-description"
                    value={form.description}
                    onChange={(e) =>
                      setForm({ ...form, description: e.target.value })
                    }
                    className="mt-1 min-h-36 text-sm leading-relaxed"
                    placeholder={copy.descriptionPlaceholder}
                    aria-describedby="circular-description-hint"
                    data-testid="circular-description"
                  />
                  <p id="circular-description-hint" className="mt-1 text-xs text-[var(--text-muted)]">
                    {copy.descriptionHint}
                  </p>
                </div>
                <section
                  className="border border-[var(--subtle)] border-l-4 bg-white px-4 py-3"
                  style={{ borderLeftColor: previewAccent }}
                  data-testid="circular-reader-preview"
                >
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">
                    {copy.readerPreview}
                  </div>
                  <div className="mt-1 text-base font-semibold text-[var(--text-primary)]">
                    {form.title || copy.previewTitle}
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[var(--text-primary)]">
                    {form.description || copy.previewDescription}
                  </p>
                </section>
                {canManageAnnouncements && (
                  <AnnouncementAttachmentPicker
                    value={form.attachments}
                    onChange={(attachments) => setForm({ ...form, attachments })}
                    disabled={isSubmitting}
                    inputId="circular-attachments"
                  />
                )}
                <div>
                  <Label htmlFor="circular-visible-until" className="text-xs uppercase font-semibold">
                    {copy.visibleUntil}
                  </Label>
                  <Input
                    id="circular-visible-until"
                    type="date"
                    value={form.visible_until}
                    onChange={(e) =>
                      setForm({ ...form, visible_until: e.target.value })
                    }
                    className="mt-1"
                    data-testid="circular-visible-until"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase font-semibold">
                    {t("staffCirculars.audience")}
                  </Label>
                  <div className="mt-2 flex flex-wrap gap-4">
                    {(teacherOnly ? ["guardian"] : ["guardian", "teacher"]).map(
                      (r) => (
                        <label
                          key={r}
                          className="flex items-center gap-2 text-sm capitalize"
                        >
                          <Checkbox
                            checked={form.audience_roles.includes(r)}
                            onCheckedChange={(c) =>
                              setForm({
                                ...form,
                                audience_roles: c
                                  ? [...form.audience_roles, r]
                                  : form.audience_roles.filter((x) => x !== r),
                              })
                            }
                            disabled={teacherOnly}
                            data-testid={`aud-${r}`}
                          />
                          {r === "guardian"
                            ? t("staffCirculars.guardians")
                            : t("staffCirculars.teachers")}
                        </label>
                      ),
                    )}
                  </div>
                </div>
                {(teacherOnly || form.audience_roles.includes("guardian")) && (
                  <div>
                    <Label className="text-xs uppercase font-semibold">
                      {teacherOnly ? t("teacher.section") : copy.gradeLabel}
                    </Label>
                    {gradeScopeLoading ? (
                      <Skeleton
                        className="h-9 w-full mt-1"
                        data-testid="circular-section-loading"
                      />
                    ) : (
                      teacherOnly ? (
                        <Select
                          value={form.section_id || undefined}
                          onValueChange={(value) =>
                            setForm({
                              ...form,
                              section_id: value,
                            })
                          }
                        >
                          <SelectTrigger
                            className="mt-1"
                            data-testid="circular-section-scope"
                          >
                            <SelectValue
                              placeholder={t("teacher.chooseSection")}
                            />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {sections.map((section) => (
                              <SelectItem key={section.id} value={section.id}>
                                {section.grade?.id
                                  ? `${levelDisplayName(section.grade, language)} · ${section.name}`
                                  : section.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Select
                          value={form.grade_id || "all"}
                          onValueChange={(value) =>
                            setForm({
                              ...form,
                              grade_id: value === "all" ? "" : value,
                            })
                          }
                        >
                          <SelectTrigger
                            className="mt-1"
                            data-testid="circular-grade-scope"
                          >
                            <SelectValue
                              placeholder={copy.chooseGrade}
                            />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            <SelectItem value="all">
                              {t("teacher.allLabel", {
                                label: t("staffCirculars.guardians"),
                                count: gradeOptions.length,
                              })}
                            </SelectItem>
                            {gradeOptions.map((grade) => (
                              <SelectItem key={grade.id} value={grade.id}>
                                {grade.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )
                    )}
                    {gradeScopeError && (
                      <p
                        className="mt-1 text-sm text-[var(--error)]"
                        role="alert"
                        data-testid="circular-section-error"
                      >
                        {errMsg(gradeScopeErrorDetail)}
                      </p>
                    )}
                  </div>
                )}
                {!editingCircular && (
                  <div className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.send_email}
                      onCheckedChange={(c) =>
                        setForm({ ...form, send_email: !!c })
                      }
                      data-testid="send-email-toggle"
                    />
                    <span>{t("staffCirculars.alsoEmail")}</span>
                  </div>
                )}
              </div>
              <DialogFooter className="shrink-0 border-t border-[var(--subtle)] bg-background px-4 py-4 sm:items-center sm:justify-between sm:px-6">
                {publishHint && (
                  <p className="text-sm text-[var(--text-muted)]" data-testid="circular-publish-hint">
                    {publishHint}
                  </p>
                )}
                <Button
                  onClick={() => {
                    if (editingCircular) {
                      updateMut.mutate();
                      return;
                    }
                    createMut.mutate();
                  }}
                  disabled={publishDisabled}
                  className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                  data-testid="publish-circular"
                >
                  {editingCircular ? (
                    <Pencil className="h-4 w-4 mr-1.5" />
                  ) : (
                    <Send className="h-4 w-4 mr-1.5" />
                  )}
                  {isSubmitting
                    ? t("staffCirculars.publishing")
                    : editingCircular
                      ? copy.saveChanges
                      : copy.publishNow}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : isError ? (
        <Card
          className="border-[var(--error)]/30"
          data-testid="circulars-error"
        >
          <CardContent className="py-10 text-center text-sm text-[var(--error)]">
            {errMsg(error)}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 stagger">
          {(data || []).map((c) => {
            const isEmergency = c.kind === "emergency";
            const bgColor = isEmergency ? "bg-red-50" : "bg-amber-50";
            const borderColor = isEmergency ? "border-red-100" : "border-amber-100";
            const iconBgColor = isEmergency ? "bg-red-100" : "bg-amber-100";
            const iconTextColor = isEmergency ? "text-red-600" : "text-amber-600";
            const accentColor = isEmergency ? "text-red-600" : "text-amber-600";
            
            return (
              <Card
                key={c.id}
                className={`${bgColor} ${borderColor} border rounded-lg hover:shadow-lg transition-all lift-card overflow-hidden`}
                data-testid={`circular-row-${c.id}`}
              >
                <CardContent className="p-6 flex flex-col h-full">
                  {/* Header with icon, title, and edit button */}
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`${iconBgColor} ${iconTextColor} h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0`}>
                        {c.kind === "emergency" ? (
                          <ShieldAlert className="h-6 w-6" />
                        ) : (
                          <Megaphone className="h-6 w-6" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[var(--text-primary)] text-base break-words leading-snug">
                          {c.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <Badge className={`${isEmergency ? 'bg-red-600' : 'bg-amber-600'} text-white capitalize border-0 text-xs`}>
                            {c.status}
                          </Badge>
                          {c.kind === "emergency" && (
                            <Badge className="bg-red-600 text-white border-0 text-xs font-medium">
                              {copy.emergencyType}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    {canManageAnnouncements && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => startEdit(c)}
                        data-testid={`edit-circular-${c.id}`}
                        className="flex-shrink-0 h-8 w-8 p-0"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  {/* Content area */}
                  <div className="flex-1 mb-4 space-y-3">
                    <div>
                      <div className={`text-xs font-medium ${accentColor} mb-1`}>
                        {t("staffCirculars.publishedOn", {
                          date: formatDate(c.publish_at || c.created_at),
                        })}
                      </div>
                      <div className="text-xs text-[var(--text-muted)]">
                        {copy.visibleUntil}: {c.visible_until ? formatDate(c.visible_until) : "-"}
                      </div>
                    </div>
                    
                    {/* Engagement metrics */}
                    <div className="flex items-center gap-4 text-xs pt-2 border-t border-current border-opacity-10">
                      <span className="inline-flex items-center gap-1.5 text-[var(--text-secondary)]">
                        <Send className="h-3.5 w-3.5" /> {t("staffCirculars.sentCount", { count: c.receipt_count || 0 })}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-[var(--success)]">
                        <CheckCheck className="h-3.5 w-3.5" /> {t("staffCirculars.readCount", { count: c.read_count || 0 })}
                      </span>
                    </div>
                  </div>
                  
                  {/* Attachments */}
                  <AnnouncementAttachmentList record={c} />
                </CardContent>
              </Card>
            );
          })}
          {(data || []).length === 0 && (
            <div className="col-span-full">
              <Card className="border-[var(--subtle)]">
                <CardContent className="py-10 text-center text-[var(--text-muted)]">
                  <Megaphone className="h-8 w-8 mx-auto mb-2 opacity-40" />{" "}
                    {forcedKind === "emergency"
                      ? t("staffCirculars.emptyEmergencyState")
                      : t("staffCirculars.emptyState")}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
